import React, { useEffect, useMemo, useRef, useState } from 'react';
import './MonthlyPlanner.css';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { IconButton } from '@mui/material';
import GitHubIcon from '@mui/icons-material/GitHub';

type Task = {
  id: string;
  taskName: string;
  assignee: string;
  effort: string;
  referenceId: string;
  comment: string;
  startDate: string; // YYYY-MM-DD
  endDate: string;   // YYYY-MM-DD
};

type DayTasks = Record<string, Task[]>; // key: YYYY-MM-DD

function formatDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  const d = `${date.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getDaysInMonth(year: number, monthIndex0: number): Date[] {
  const first = new Date(year, monthIndex0, 1);
  const days: Date[] = [];
  let current = first;
  while (current.getMonth() === monthIndex0) {
    days.push(new Date(current));
    current = new Date(current.getFullYear(), current.getMonth(), current.getDate() + 1);
  }
  return days;
}

export default function MonthlyPlanner(): React.ReactElement {
  const today = new Date();
  const [year, setYear] = useState<number>(today.getFullYear());
  const [monthIndex, setMonthIndex] = useState<number>(today.getMonth()); // 0-11
  const [dayTasks, setDayTasks] = useState<DayTasks>({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [activeDay, setActiveDay] = useState<string | null>(null); // YYYY-MM-DD
  const [form, setForm] = useState<Omit<Task, 'id'>>({ taskName: '', assignee: '', effort: '', referenceId: '', comment: '', startDate: '', endDate: '' });

  const days = useMemo(() => getDaysInMonth(year, monthIndex), [year, monthIndex]);

  // Storage keys and helpers
  const STORAGE_KEY = 'monthlyPlanner.tasks.v1';

  function normalizeDayTasks(input: DayTasks): DayTasks {
    const output: DayTasks = {};
    for (const [dateKey, tasks] of Object.entries(input)) {
      output[dateKey] = tasks.map(t => ({
        id: t.id,
        taskName: t.taskName,
        assignee: t.assignee,
        effort: t.effort,
        referenceId: t.referenceId,
        comment: t.comment,
        startDate: (t as any).startDate ?? dateKey,
        endDate: (t as any).endDate ?? dateKey,
      }));
    }
    return output;
  }

  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      setDayTasks(normalizeDayTasks(parsed as DayTasks));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dayTasks));
  }, [dayTasks]);

  function handleAdd(dayKey: string) {
    if (!form.taskName.trim()) return;
    setDayTasks(prev => {
      const existing = prev[dayKey] ?? [];
      const start = form.startDate || dayKey;
      const end = form.endDate || dayKey;
      const newTask: Task = { id: `${Date.now()}-${Math.random().toString(36).slice(2,8)}`, taskName: form.taskName, assignee: form.assignee, effort: form.effort, referenceId: form.referenceId, comment: form.comment, startDate: start, endDate: end };
      return { ...prev, [dayKey]: [...existing, newTask] };
    });
    setForm({ taskName: '', assignee: '', effort: '', referenceId: '', comment: '', startDate: '', endDate: '' });
  }

  function handleRemove(dayKey: string, taskId: string) {
    setDayTasks(prev => {
      const remaining = (prev[dayKey] ?? []).filter(t => t.id !== taskId);
      const next: DayTasks = { ...prev };
      if (remaining.length) next[dayKey] = remaining; else delete next[dayKey];
      return next;
    });
  }

  function openDayModal(date: Date) {
    const key = formatDateKey(date);
    setActiveDay(key);
    setForm(prev => ({ ...prev, startDate: key, endDate: key }));
  }

  function closeModal() {
    setActiveDay(null);
    setForm({ taskName: '', assignee: '', effort: '', referenceId: '', comment: '', startDate: '', endDate: '' });
  }

  function exportTasks() {
    const dataStr = JSON.stringify(dayTasks, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const ts = new Date();
    const name = `monthly-planner-${ts.getFullYear()}-${(ts.getMonth()+1).toString().padStart(2,'0')}-${ts.getDate().toString().padStart(2,'0')}.json`;
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  function exportToPDF() {
    const doc = new jsPDF();
    const ts = new Date();
    const monthName = monthOptions[monthIndex];
    
    // Add header
    doc.setFontSize(20);
    doc.text('Monthly Planner Report', 20, 20);
    
    // Add month and year
    doc.setFontSize(14);
    doc.text(`${monthName} ${year}`, 20, 35);
    
    // Add generation date
    doc.setFontSize(10);
    doc.text(`Generated on: ${ts.toLocaleDateString()}`, 20, 45);
    
    // Get all tasks sorted by date
    const allTasks = Object.entries(dayTasks)
      .sort(([a], [b]) => a.localeCompare(b))
      .flatMap(([dateKey, tasks]) => 
        tasks.map(task => ({
          date: dateKey,
          taskName: task.taskName,
          assignee: task.assignee,
          effort: task.effort,
          referenceId: task.referenceId,
          startDate: task.startDate,
          endDate: task.endDate,
          comment: task.comment
        }))
      );

    if (allTasks.length === 0) {
      doc.setFontSize(12);
      doc.text('No tasks found for this month.', 20, 60);
    } else {
      // Prepare table data
      const tableData = allTasks.map(task => [
        task.date,
        task.taskName,
        task.assignee || '-',
        task.effort || '-',
        task.referenceId || '-',
        task.startDate,
        task.endDate,
        task.comment || '-'
      ]);

      // Add table
      autoTable(doc, {
        head: [['Date', 'Task Name', 'Assignee', 'Effort', 'Reference ID', 'Start Date', 'End Date', 'Comment']],
        body: tableData,
        startY: 55,
        styles: {
          fontSize: 8,
          cellPadding: 3
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        columnStyles: {
          0: { cellWidth: 20 }, // Date
          1: { cellWidth: 35 }, // Task Name
          2: { cellWidth: 20 }, // Assignee
          3: { cellWidth: 15 }, // Effort
          4: { cellWidth: 20 }, // Reference ID
          5: { cellWidth: 20 }, // Start Date
          6: { cellWidth: 20 }, // End Date
          7: { cellWidth: 30 }  // Comment
        },
        margin: { left: 20, right: 20 },
      });

      // Add summary after table (estimate position)
      const estimatedTableHeight = 55 + (tableData.length * 8) + 20; // Rough estimate
      const summaryY = Math.min(estimatedTableHeight, 250); // Don't go too far down
      
      doc.setFontSize(12);
      doc.text(`Total Tasks: ${allTasks.length}`, 20, summaryY);
      
      // Count tasks by assignee
      const assigneeCounts = allTasks.reduce((acc, task) => {
        const assignee = task.assignee || 'Unassigned';
        acc[assignee] = (acc[assignee] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      
      if (Object.keys(assigneeCounts).length > 0) {
        doc.text('Tasks by Assignee:', 20, summaryY + 10);
        Object.entries(assigneeCounts).forEach(([assignee, count], index) => {
          doc.setFontSize(10);
          doc.text(`• ${assignee}: ${count} task${count > 1 ? 's' : ''}`, 25, summaryY + 20 + (index * 8));
        });
      }
    }

    // Save the PDF
    const fileName = `monthly-planner-report-${monthName.toLowerCase()}-${year}.pdf`;
    doc.save(fileName);
  }

  function triggerImport() {
    fileInputRef.current?.click();
  }

  function isValidTask(obj: unknown): obj is Task {
    if (!obj || typeof obj !== 'object') return false;
    const t = obj as Partial<Task>;
    return (
      typeof t.id === 'string' &&
      typeof t.taskName === 'string' &&
      typeof t.assignee === 'string' &&
      typeof t.effort === 'string' &&
      typeof t.referenceId === 'string' &&
      typeof t.comment === 'string' &&
      (typeof (t as any).startDate === 'undefined' || typeof (t as any).startDate === 'string') &&
      (typeof (t as any).endDate === 'undefined' || typeof (t as any).endDate === 'string')
    );
  }

  function validateImportedData(data: unknown): data is DayTasks {
    if (!data || typeof data !== 'object') return false;
    const obj = data as Record<string, unknown>;
    const dateKeyOk = Object.keys(obj).every(k => /^\d{4}-\d{2}-\d{2}$/.test(k));
    if (!dateKeyOk) return false;
    return Object.values(obj).every(v => Array.isArray(v) && (v as unknown[]).every(isValidTask));
  }

  function onImportFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result as string);
        if (validateImportedData(parsed)) {
          setDayTasks(normalizeDayTasks(parsed));
        } else {
          alert('Invalid tasks file format.');
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Import failed to parse JSON', err);
        alert('Failed to parse JSON file.');
      } finally {
        fileInputRef.current?.setAttribute('value', '');
      }
    };
    reader.readAsText(file);
  }

  const monthOptions = [
    'January','February','March','April','May','June','July','August','September','October','November','December'
  ];

  return (
    <div className="mp-root">
      <IconButton
        href="https://github.com/release-pilot/release-pilot-public/tree/master/monthly-planner"
        target="_blank"
        rel="noopener"
        color="primary"
        style={{ position: 'absolute', top: 0, right: 0, margin: '10px', zIndex: 1000 }}
      >
        <GitHubIcon fontSize="large" />
      </IconButton>
      <div className="mp-header">
        <img className="mp-logo" src="/assets/Single_mainlogo_tm.svg" alt="Release Pilot" />
        <h2 className="mp-title">Monthly Planner</h2>
      </div>
      <div className="mp-controls">
        <select value={monthIndex} onChange={e => setMonthIndex(parseInt(e.target.value, 10))}>
          {monthOptions.map((label, idx) => (
            <option key={label} value={idx}>{label}</option>
          ))}
        </select>
        <input type="number" value={year} onChange={e => setYear(parseInt(e.target.value || `${today.getFullYear()}`, 10))} style={{ width: 100 }} />
        <button className="mp-primary-btn" onClick={exportTasks}>Export JSON</button>
        <button className="mp-primary-btn" onClick={exportToPDF}>Export PDF</button>
        <button className="mp-add-btn" onClick={triggerImport}>Import</button>
        <input ref={fileInputRef} type="file" accept="application/json" onChange={onImportFileChange} style={{ display: 'none' }} />
      </div>

      <div className="mp-grid">
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
          <div key={d} className="mp-weekday">{d}</div>
        ))}

        {(() => {
          const firstDay = new Date(year, monthIndex, 1).getDay();
          const blanks = Array.from({ length: firstDay }, (_, i) => <div key={`b-${i}`} />);
          const cells = days.map(date => {
            const key = formatDateKey(date);
            const count = dayTasks[key]?.length ?? 0;
            return (
              <div key={key} className="mp-cell">
                <div className="mp-cell-header">
                  <div className="mp-day">
                    {date.getDate()}
                    {count > 0 ? <span className="mp-task-dot" /> : null}
                  </div>
                  <button className="mp-add-btn" onClick={() => openDayModal(date)}>Add</button>
                </div>
                <div className="mp-count">
                  {(() => {
                    if (count === 0) return 'No tasks';
                    const suffix = count > 1 ? 's' : '';
                    return `${count} task${suffix}`;
                  })()}
                </div>
              </div>
            );
          });
          return [...blanks, ...cells];
        })()}
      </div>

      {activeDay && (
        <div className="mp-overlay">
          <div className="mp-modal">
            <div className="mp-modal-header">
              <h3 className="mp-modal-title">Tasks on {activeDay}</h3>
              <button className="mp-close-btn" onClick={closeModal}>Close</button>
            </div>

            <div className="mp-form">
              <input placeholder="Task name" value={form.taskName} onChange={e => setForm({ ...form, taskName: e.target.value })} />
              <input placeholder="Assignee" value={form.assignee} onChange={e => setForm({ ...form, assignee: e.target.value })} />
              <input placeholder="Effort" value={form.effort} onChange={e => setForm({ ...form, effort: e.target.value })} />
              <input placeholder="Reference ID" value={form.referenceId} onChange={e => setForm({ ...form, referenceId: e.target.value })} />
              <input placeholder="Start date (YYYY-MM-DD)" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} />
              <input placeholder="End date (YYYY-MM-DD)" value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })} />
              <input placeholder="Comment" value={form.comment} onChange={e => setForm({ ...form, comment: e.target.value })} style={{ gridColumn: '1 / span 2' }} />
              <div className="mp-form-actions">
                <button className="mp-primary-btn" onClick={() => activeDay && handleAdd(activeDay)}>Add Task</button>
              </div>
            </div>

            <div style={{ marginTop: 16 }}>
              {(dayTasks[activeDay] ?? []).length === 0 ? (
                <div style={{ color: '#777' }}>No tasks yet.</div>
              ) : (
                <table className="mp-table">
                  <thead>
                    <tr>
                      <th>Task</th>
                      <th>Assignee</th>
                      <th>Effort</th>
                      <th>Ref</th>
                      <th>Start</th>
                      <th>End</th>
                      <th>Comment</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(dayTasks[activeDay] ?? []).map(t => (
                      <tr key={t.id}>
                        <td>{t.taskName}</td>
                        <td>{t.assignee}</td>
                        <td>{t.effort}</td>
                        <td>{t.referenceId}</td>
                        <td>{t.startDate}</td>
                        <td>{t.endDate}</td>
                        <td>{t.comment}</td>
                        <td>
                          <button className="mp-danger-btn" onClick={() => handleRemove(activeDay, t.id)}>Remove</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bottom aggregated list by date */}
      <div className="mp-task-list">
        {Object.keys(dayTasks).length > 0 ? (
          <>
            <h3 className="mp-task-list-title">All Tasks by Date</h3>
            {Object.entries(dayTasks)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([dateKey, tasks], groupIndex) => (
                <div key={dateKey} className="mp-date-group" style={{ animationDelay: `${groupIndex * 100}ms` }}>
                  <div className="mp-date-header">Date: {dateKey}</div>
                  {tasks.map((t, taskIndex) => (
                    <div key={t.id} className="mp-task-card" style={{ animationDelay: `${taskIndex * 150}ms` }}>
                      <div className="mp-task-header">
                        <div className="mp-task-name">{t.taskName}</div>
                      </div>
                      <div className="mp-task-meta">
                        {t.assignee && (
                          <div className="mp-meta-item assignee">
                            👤 {t.assignee}
                          </div>
                        )}
                        {t.effort && (
                          <div className="mp-meta-item effort">
                            ⏱️ {t.effort}
                          </div>
                        )}
                        {t.referenceId && (
                          <div className="mp-meta-item ref">
                            🔗 {t.referenceId}
                          </div>
                        )}
                        <div className="mp-meta-item dates">
                          📅 {t.startDate} → {t.endDate}
                        </div>
                      </div>
                      {t.comment && (
                        <div className="mp-task-comment">
                          💬 {t.comment}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
          </>
        ) : (
          <div className="mp-empty-state">
            📝 No tasks created yet. Click "Add" on any day to get started!
          </div>
        )}
      </div>
    </div>
  );
}


